// SessionLlmPickerModal — Iter160e (Randy pt-ytre-p1).
//
// A session-only variant of AiAssistantModelPicker. Instead of PATCHing
// the silo's `surface_tune` override (which persists forever), the user's
// pick lives in sessionStorage under `mysilo_session_llm_{surface}` — so:
//   • Same tab, page navigation → pick persists
//   • Reload → back to platform / silo default
//   • Close tab → wiped
//   • Logout → wiped
//
// Mounts today on Public Q&A + YTRE (both surfaces share `public_ask`).
// Reuses ModelOutputPanel so the UI matches the persistent picker
// pixel-for-pixel; the only differences are (a) save writes to
// sessionStorage, (b) "Reset" clears the sessionStorage entry rather
// than restoring silo defaults on the backend.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import ModelOutputPanel from "@/components/ModelOutputPanel";
import {
  readSessionLlmOverride,
  writeSessionLlmOverride,
  clearSessionLlmOverride,
} from "@/lib/sessionLlm";

const providerFor = (m) => {
  if (!m) return "gemini";
  if (m.startsWith("gemini")) return "gemini";
  if (m.startsWith("gpt")) return "openai";
  if (m.startsWith("claude")) return "anthropic";
  return "gemini";
};

export default function SessionLlmPickerModal({
  surface = "public_ask",
  siloId = null,
  siloSlug = null,
  onClose,
  onSaved,
}) {
  const [registry, setRegistry] = useState(null);
  const [value, setValue] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        // Kick off the registry fetch alongside whatever surface metadata
        // we can find so the picker seeds with the CURRENT effective
        // model (session-override → silo → platform default).
        const promises = [api.get("/models/registry")];
        if (siloId) {
          promises.push(api.get(`/silos/${siloId}/surface-tune`).catch(() => null));
        }
        const [regResp, tuneResp] = await Promise.all(promises);
        if (cancel) return;
        const reg = regResp?.data;
        const surfaceRow = (tuneResp?.data?.surfaces || []).find((s) => s.surface === surface) || {};
        const platformDefault = reg?.platform_default_model || "gemini-2.5-flash";
        // Preferred seed order: session override → silo override → platform default
        const override = readSessionLlmOverride(surface);
        const seedModel = override?.model || surfaceRow.model || platformDefault;
        const seedMax = override?.max_output_tokens ?? surfaceRow.max_output_tokens ?? 4096;
        const seedDefault = surfaceRow.default_max_output_tokens ?? surfaceRow.max_output_tokens ?? 4096;
        const seed = {
          model_name: seedModel,
          provider: providerFor(seedModel),
          max_output_tokens: seedMax,
          default_max_output_tokens: seedDefault,
        };
        setRegistry(reg);
        setValue(seed);
        setSnapshot(seed);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load the model registry.");
      }
    })();
    return () => { cancel = true; };
  }, [surface, siloId]);

  const save = () => {
    if (!value?.model_name) return;
    setBusy(true);
    try {
      writeSessionLlmOverride(surface, {
        model: value.model_name,
        max_output_tokens: value.max_output_tokens,
      });
      toast.success("Model picked for this browser tab. Refresh reverts to default.");
      onSaved?.({ model: value.model_name, max_output_tokens: value.max_output_tokens });
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setBusy(true);
    try {
      clearSessionLlmOverride(surface);
      toast.success("Reverted to the platform default for this tab.");
      onSaved?.(null);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[240] flex items-center justify-center bg-black/40 p-4"
      data-testid="session-llm-picker-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#FDFCF8] border hairline rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b hairline sticky top-0 bg-[#FDFCF8] z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#2F6B3A] font-semibold">This tab only</div>
            <div className="text-sm font-medium text-[#1C1B1A] mt-0.5">Model &amp; output — session pick</div>
          </div>
          <button
            onClick={onClose}
            data-testid="session-llm-picker-close"
            className="p-1 text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4">
          {value ? (
            <ModelOutputPanel
              scope="session"
              heading="Choose a model for this tab"
              subtitle={
                "Session-only override — the pick is remembered as long as this tab stays open. Refreshing the page or closing the tab reverts to the platform / silo default. Nothing is saved to the silo."
              }
              value={value}
              defaults={snapshot}
              registry={registry}
              busy={busy}
              onChange={setValue}
              onSave={save}
              onResetModel={reset}
            />
          ) : (
            <div className="text-xs text-[#6B6A68] py-8 text-center">Loading…</div>
          )}
        </div>
      </div>
    </div>
  );
}
